import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";

import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    listField = "listField",
    image = "image",
    title = "title",
    subTitle = "subTitle",
    description = "description",
    background = "background",
    link = "link",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.listField]: any[];
        [fields.image]: string;
        [fields.title]: string;
        [fields.subTitle]: string;
        [fields.description]: string;
        [fields.background]: string;
        [fields.link]: LinkFieldValue;
    };
}

type Props = OwnProps;

export interface IBannerInfoCardsStyles {
    container?: GridContainerProps;
    infoCard?: GridItemProps;
    title?: TypographyProps;
    content?: InjectableCss;
    desc?: InjectableCss;
    imageContainer?: InjectableCss;
    linkStyles?: LinkPresentationProps;
}

export const BannerInfoCardsStyles: IBannerInfoCardsStyles = {
    container: {
        css: css`
            align-items: center;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 720px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 1300px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    infoCard: {
        width: [12, 12, 4, 4, 4],
        css: css`
            // margin-right: 38px !important;
            // :nth-child(3n) {
            //     margin-right: 0px !important;
            // }
            /* :only-child {
                margin-right: 0px;
                //width: 230px;
                img {
                    height: 200px;
                    width: 100%;
                    object-fit: cover;
                }
            } */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px;
                        `,
                        css`
                            padding: 15px;
                        `,
                        css`
                            padding: 19px;
                        `,
                        css`
                            padding: 19px;
                        `,
                        css`
                            padding: 19px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    title: {
        variant: "h5",
        css: css`
            color: #fff;
            text-transform: uppercase;
            font-size: 1.3rem;
            letter-spacing: 1px;
            margin-bottom: 5px;
            line-height: 23px;
            white-space: nowrap;
            .subtitle {
                display: block;
                font-weight: 200;
                font-size: 15px;
            }
        `,
    },
    imageContainer: {
        css: css`
            width: 50%;
        `,
    },
    content: {
        css: css`
            width: 50%;
            padding: 0 10px;
        `,
    },
    desc: {
        css: css`
            display: block;
            line-height: 16px;
            font-size: 13px;
            color: #fff;
            font-weight: 400;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        `,
    },
    linkStyles: {
        css: css`
            margin-top: 10px;
            span {
                line-height: 16px;
                font-size: 13px;
                color: #fff !important;
                text-transform: uppercase;
                :hover {
                    color: #d3d3d3 !important;
                }
            }
        `,
    },
};

const styles = BannerInfoCardsStyles;

const DescriptionWrapper = styled.div<{ backgroundColor: string }>`
    background-color: ${props => props.backgroundColor};
    padding: 15px 0;
`;

const BannerInfoCards: React.FC<Props> = ({ fields }) => {
    const infoLinks = useGetLinks(fields.listField, o => o.fields.link);

    return (
        <DescriptionWrapper backgroundColor={fields.background}>
            <GridContainer {...styles.container}>
                {fields.listField.map((item, index: number) => (
                    <GridItem key={item.fields.title} {...styles.infoCard}>
                        <StyledWrapper {...styles.imageContainer}>
                            <LazyImage src={item.fields.image} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.content}>
                            <Typography {...styles.title}>
                                {item.fields.title} <span className="subtitle">{item.fields.subTitle}</span>
                            </Typography>
                            <Typography {...styles.desc}>{parse(item.fields.description, parserOptions)}</Typography>
                            {infoLinks[index].url ? (
                                <Link {...styles.linkStyles} href={infoLinks[index].url}>
                                    Learn More
                                </Link>
                            ) : null}
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
        </DescriptionWrapper>
    );
};

const membersList: WidgetModule = {
    component: BannerInfoCards,
    definition: {
        group: "Common",

        fieldDefinitions: [
            {
                name: fields.listField,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",

                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.subTitle,
                        displayName: "Sub Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.image,
                        displayName: "Photo",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: fields.description,
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        sortOrder: 2,
                    },
                    {
                        name: fields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        sortOrder: 3,
                    },
                ],
            },
            {
                fieldType: "Translatable",
                name: fields.background,
                displayName: "Description Background",
                editorTemplate: "ColorPickerField",
                defaultValue: "white",
                sortOrder: 1,
            },
        ],
    },
};

export default membersList;
